import type { Component } from "solid-js";
import { createEffect } from "solid-js";
import { createSolidStreamingMarkdown, SolidStreamingMarkdown } from "./solid-markdown";
import "./solid-markdown/code-highlighting/prism-github.css";
import "./solid-markdown/code-highlighting/prism-github-dark.css";

export {
  createSolidStreamingMarkdown,
  markdownToHtml,
  parseMarkdown,
  SolidStreamingMarkdown,
  stripMarkdownToPlainText,
} from "./solid-markdown";
export { HighlightedCodeBlock } from "./solid-markdown/code-highlighting/HighlightedCodeBlock";
export {
  MarkdownClipboardProvider,
  type MarkdownRendererOptions,
  SolidStreamingMarkdownChildren,
} from "./solid-markdown/components/ASTRenderers";

/**
 * A streaming markdown component that renders markdown content incrementally.
 *
 * Pass the full content string via the `content` prop; the component appends whatever grew
 * since the last render rather than reparsing the whole document, so it is designed to render
 * once and then update only as deltas arrive from the server.
 *
 * @example
 * ```tsx
 * const [content, setContent] = createSignal("");
 *
 * // Server sends: "Hello"
 * setContent("Hello");
 * // Server sends: " world"
 * setContent("Hello world");
 *
 * <StreamingMarkdown content={content()} />
 * ```
 *
 * **Important behavioral notes:**
 * - The component renders immediately and updates incrementally
 * - Only changed portions of the markdown are re-rendered for performance
 * - Content must only grow — the parser keeps its state between renders
 */
export const StreamingMarkdown: Component<{
  content?: string;
}> = (props) => {
  const streamer = createSolidStreamingMarkdown();

  createEffect((oldContent: string | undefined) => {
    const content = props.content;

    if (content !== undefined) {
      if (oldContent !== content) {
        streamer.append(content.substring(oldContent?.length || 0));
      }
      return content;
    }

    return oldContent;
  });

  return (
    <SolidStreamingMarkdown
      doc={streamer.doc}
      options={{
        classes: {
          document: "**:animate-fade-in wrap-break-word whitespace-break-spaces px-4",
          heading: (node) => {
            switch (node.level) {
              case 1:
                return "font-medium text-3xl mb-3 mt-4";
              case 2:
                return "font-medium text-2xl mb-3 mt-4";
              case 3:
                return "font-medium text-xl mb-3 mt-4";
              case 4:
                return "font-medium text-lg mb-3 mt-4";
              case 5:
                return "font-medium text-base mb-3 mt-4";
              case 6:
                return "font-medium text-sm mb-3 mt-4";
              default:
                return "";
            }
          },
          list: (node) => {
            switch (node.listData?.type) {
              case "ordered":
                return "list-decimal pl-4";
              case "bullet":
                return "list-disc pl-4";
              default:
                return "";
            }
          },
          paragraph: "mb-4",
          item: (node) => {
            if (!node.listData?.task) return "mb-2";
            // The marker is dropped per item, not per list, so a plain sibling keeps its bullet and an
            // ordered checklist keeps its number. Inlining the first paragraph puts the checkbox beside
            // the text while leaving the <li> a list item, so nested content still stacks underneath.
            const marker = node.listData.type === "ordered" ? "" : "list-none ";
            return `${marker}mb-2 [&>p:first-of-type]:inline [&>.markdown-task-checkbox]:mr-2 [&>.markdown-task-checkbox]:align-middle`;
          },
          code: "bg-black/10 dark:bg-white/10 px-0.5 py-0.5 rounded text-sm",
          blockQuote: "border-l-2 border-indigo-100 dark:border-indigo-700 pl-2 py-1 my-1",
          tableWrapper: "overflow-x-auto w-full max-w-full rounded-lg border border-muted-border",
          table: "",
          tableRow: "border-b border-muted-border [&:last-child]:border-b-0",
          tableCell: "px-2 py-2 border-r border-muted-border [&:last-child]:border-r-0",
          tableHead: "bg-panel text-sm font-semibold [&_td]:whitespace-nowrap",
          tableBody: "bg-tile",
          link: "text-brand underline",
        },
      }}
    />
  );
};
